import useSWR from "swr";

import { useCallback, useEffect, useMemo, useState } from "react";

import Bugsnag from "@bugsnag/js";
import { useUpdateEffect } from "usehooks-ts";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher, fetchApi } from "utils/baseFetcher";

import {
  getContentVertivalsUrl,
  getCountriesUrl,
  getGeographiesUrl,
  getSectorsUrl,
  getSubscribedVerticalsUrl,
  MARKET_CAPS,
} from "./utils";

const useFeedSettings = (options?: any) => {
  const { autoSave, onChange } = options || {};

  const { accountId, account, mutateAccount } = useAuthentication();
  const { setNotification } = useNotification();

  const [activeTab, setActiveTab] = useState("interests");

  const [selectedSectors, setSelectedSectors] = useState<any[]>([]);
  const [selectedContentVerticals, setSelectedContentVerticals] = useState<
    any[]
  >([]);
  const [selectedGeographies, setSelectedGeographies] = useState<any[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
  const [selectedMarketCap, setSelectedMarketCap] = useState(MARKET_CAPS[0]);

  const [savingSettings, setSavingSettings] = useGlobalState(
    "savingFeedSettings",
    false
  );

  const selectedMandates: any = useMemo(
    () => ({
      sectors: selectedSectors,
      contentVerticals: selectedContentVerticals,
      geographies: selectedGeographies,
      countries: selectedCountries,
    }),
    [
      selectedContentVerticals,
      selectedCountries,
      selectedGeographies,
      selectedSectors,
    ]
  );

  const [_showFeedSettingsModal, setShowFeedSettings] = useGlobalState(
    "showFeedSettingsModal",
    false
  );

  const [_refreshingReadinglist, setRefreshingReadinglist] = useGlobalState(
    "refreshingReadinglist",
    false
  );

  const [_isSaveFeedSettingsDisabled, setIsSaveFeedSettingsDisabled] =
    useGlobalState("isSaveFeedSettingsDisabled", false);

  const isSaveDisabled = useMemo(
    () =>
      (selectedMandates.geographies?.length === 0 &&
        selectedMandates.countries?.length === 0) ||
      selectedMandates.sectors?.length === 0 ||
      selectedMandates.contentVerticals?.length === 0,
    [
      selectedMandates.contentVerticals?.length,
      selectedMandates.countries?.length,
      selectedMandates.geographies?.length,
      selectedMandates.sectors?.length,
    ]
  );
  useEffect(() => {
    setIsSaveFeedSettingsDisabled(isSaveDisabled);
  }, [isSaveDisabled, setIsSaveFeedSettingsDisabled]);

  const { data: geographies = [] } = useSWR(getGeographiesUrl(), baseFetcher);

  const { data: rawCountries = [] } = useSWR(getCountriesUrl(), baseFetcher);
  const countries = useMemo(
    () =>
      rawCountries?.sort?.((a: any, b: any) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      }) || [],
    [rawCountries]
  );

  const { data: sectors = [] } = useSWR(getSectorsUrl(), baseFetcher);

  const { data: contentVerticals = [] } = useSWR(
    getContentVertivalsUrl(),
    baseFetcher
  );

  const { data: subscribedVerticals = [] } = useSWR(
    account && getSubscribedVerticalsUrl(accountId!),
    baseFetcher
  );

  useEffectOnce(
    (ack) => {
      (async () => {
        if (!account) return;

        ack();
        setSelectedCountries(account.countryMandates ?? []);
        setSelectedGeographies(account.geographyMandates ?? []);
        setSelectedSectors(account.sectorMandates);

        const { maxMarketCap, minMarketCap } = account;
        const selectedMarketCap =
          MARKET_CAPS.find(
            (i) => i.min === minMarketCap && i.max === maxMarketCap
          ) || MARKET_CAPS[0];
        setSelectedMarketCap(selectedMarketCap!);
      })();
    },
    [account]
  );

  useEffect(() => {
    if (!subscribedVerticals?.length) {
      return;
    }
    setSelectedContentVerticals(subscribedVerticals?.map((i: any) => i.id));
  }, [subscribedVerticals]);

  const requestBody = useMemo(
    () => ({
      data: {
        id: accountId,
        attributes: {
          ...(selectedMarketCap
            ? {
                "min-market-cap": selectedMarketCap.min,
                "max-market-cap": selectedMarketCap.max,
              }
            : {}),
          "geography-mandates": selectedGeographies,
          "sector-mandates": selectedSectors,
          "country-mandates": selectedCountries,
        },
        relationships: {
          "subscribed-verticals": {
            data: selectedContentVerticals.map((i: any) => ({
              type: "content-verticals",
              id: i,
            })),
          },
        },
        type: "accounts",
      },
    }),
    [
      accountId,
      selectedMarketCap,
      selectedGeographies,
      selectedSectors,
      selectedCountries,
      selectedContentVerticals,
    ]
  );

  const onChangeCheckbox = useCallback(
    (type: string, id: string, { checked: isChecked }: any) => {
      const data = isChecked
        ? [...(selectedMandates[type] || []), id]
        : selectedMandates[type].filter(
            (i: any) => i.toLowerCase() !== id.toLowerCase()
          );

      switch (type) {
        case "geographies":
          setSelectedGeographies(data);
          break;
        case "sectors":
          setSelectedSectors(data);
          break;
        case "contentVerticals":
          setSelectedContentVerticals(data);
          break;
      }
    },
    [selectedMandates]
  );

  const onSelectDeselectAll = useCallback(
    (type: string, isSelect = true) => {
      const extractor = (i: any) => i.id;

      switch (type) {
        case "geographies":
          setSelectedGeographies(
            isSelect ? geographies.map((i: any) => i.name) : []
          );
          break;
        case "countries":
          setSelectedCountries(
            isSelect ? countries.map((i: any) => i.name) : []
          );
          break;
        case "sectors":
          setSelectedSectors(isSelect ? sectors.map((i: any) => i.name) : []);
          break;
        case "contentVerticals":
          setSelectedContentVerticals(
            isSelect ? contentVerticals.map(extractor) : []
          );
          break;
      }
    },
    [contentVerticals, countries, geographies, sectors]
  );

  const onSave = useCallback(async () => {
    if (isSaveDisabled) return;

    setSavingSettings(true);

    try {
      await fetchApi(`/api/v2/accounts/${accountId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody),
      });
      if (!autoSave) setNotification("Feed settings updated!", "success");
      mutateAccount(
        {
          ...account,
          countryMandates: selectedMandates.countries,
          geographyMandates: selectedMandates.geographies,
          sectorMandates: selectedMandates.sectors,
          minMarketCap: selectedMarketCap.min,
          maxMarketCap: selectedMarketCap.max,
        },
        { revalidate: false }
      );
    } catch (error: any) {
      Bugsnag.notify(error);
      if (!autoSave) setNotification("Feed settings saving failed!", "error");
    } finally {
      setSavingSettings(false);
      // setRefreshingReadinglist(true);
      if (!autoSave) setShowFeedSettings(false);
    }
  }, [
    account,
    accountId,
    autoSave,
    isSaveDisabled,
    mutateAccount,
    requestBody,
    selectedMandates.countries,
    selectedMandates.geographies,
    selectedMandates.sectors,
    selectedMarketCap.max,
    selectedMarketCap.min,
    setNotification,
    setSavingSettings,
    setShowFeedSettings,
  ]);

  useUpdateEffect(() => {
    onChange?.();
    const timer = setTimeout(() => {
      if (autoSave) {
        onSave();
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [requestBody]);

  const onChangeActiveTab = useCallback((name: string) => {
    setActiveTab(name);
  }, []);

  const onSelectMarketCap = useCallback((item: any) => {
    setSelectedMarketCap(item);
  }, []);

  const onChangeCountries = useCallback((data: any) => {
    setSelectedCountries((oldState: any) => {
      if (!oldState.find((i: string) => i === data.name)) {
        oldState = oldState.concat(data.name);
      }
      return oldState;
    });
  }, []);

  const onRemoveCountry = useCallback((item: string) => {
    setSelectedCountries((oldState: any) => {
      const filteredCountries = oldState.filter((i: string) => i !== item);
      oldState = filteredCountries;
      return oldState;
    });
  }, []);

  return {
    geographies,
    countries,
    sectors,
    contentVerticals,
    activeTab,
    selectedMarketCap,

    setSelectedCountries,

    onSelectDeselectAll,
    onChangeCheckbox,
    onChangeActiveTab,
    onSave,
    onSelectMarketCap,
    savingSettings,
    onChangeCountries,
    onRemoveCountry,

    selectedMandates,
    isSaveDisabled,
  };
};

export default useFeedSettings;
